import { Trans, useLingui } from '@lingui/react/macro';
import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'store/store';
import UDButton from 'ud-ui/components/button';
import Modal from 'ud-ui/components/modal';
import UDText from 'ud-ui/components/text';
import UrlInput from 'ud-ui/components/url-input/url-input';
import { GameDetails, GameEvent } from 'modules/games/domain/interfaces/game-details';
import { setMomentUrl, setTimestampUrl } from 'modules/games/store/game-details';
import generateMomentUrl from 'modules/games/utils/generate-moment-url';
import { theme } from 'styles/theme/theme';

interface IProps {
  isLoading: boolean;
  game: GameDetails | null;
}

const keyFirstPeriod = '1';
const keySecondPeriod = '2';

export default function TimestampUrls(props: IProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useLingui();

  const { isLoading, game } = props;
  const timestampUrls = game?.timestamps ?? {};
  const firstPeriodRef = React.createRef<HTMLInputElement>();
  const secondPeriodRef = React.createRef<HTMLInputElement>();

  const [isOpen, setIsOpen] = useState(false);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  const onClose = useCallback(() => {
    close();
    dispatch(setTimestampUrl({ key: keyFirstPeriod, url: firstPeriodRef.current?.value ?? '' }));
    dispatch(setTimestampUrl({ key: keySecondPeriod, url: secondPeriodRef.current?.value ?? '' }));
  }, [close, dispatch, firstPeriodRef, secondPeriodRef]);

  const handleReplaceEventUrl = useCallback(
    (events: GameEvent[]) => {
      for (const event of events) {
        switch (event.period) {
          case 1:
            const eventUrl = generateMomentUrl({ event, periodStartUrl: firstPeriodRef.current?.value ?? '' });
            if (!eventUrl) return;
            dispatch(setMomentUrl({ eventId: event.id, url: eventUrl }));
            break;
          case 2:
            const eventUrl2 = generateMomentUrl({ event, periodStartUrl: secondPeriodRef.current?.value ?? '' });
            if (!eventUrl2) return;
            dispatch(setMomentUrl({ eventId: event.id, url: eventUrl2 }));
            break;
        }
      }
    },
    [dispatch, firstPeriodRef, secondPeriodRef],
  );

  const handleReplaceAllEventUrl = useCallback(() => {
    handleReplaceEventUrl(game?.events ?? []);
    close();
  }, [close, game?.events, handleReplaceEventUrl]);

  const handleReplaceEmptyEventUrl = useCallback(() => {
    handleReplaceEventUrl(game?.events.filter((event) => !event.videoUrl) ?? []);
    close();
  }, [close, game?.events, handleReplaceEventUrl]);

  return (
    <>
      <div className={'flex flex-col'}>
        <div className={'mb-4 flex w-full justify-end'}>
          <UDButton variant={'secondary'} onClick={open}>
            <Trans>Сгенерировать ссылки</Trans>
          </UDButton>
        </div>
      </div>

      <Modal show={isOpen} onClose={onClose} size="md">
        <Modal.Body>
          <div className={'flex flex-col gap-2'}>
            <div className={'flex flex-row gap-2'}>
              <UDText type={'subhead'}>
                <Trans>
                  Ссылки <strong>с тайм кодом</strong> на начало матча
                </Trans>
              </UDText>
            </div>
            <UDText color={'#828282'} type={'caption-1'}>
              <Trans>Ссылки принимаются из youtube и vk-video</Trans>
            </UDText>
            <UrlInput
              ref={firstPeriodRef}
              isLoading={isLoading}
              name="first-time-url"
              placeholder={t`Ссылка на начало 1-ого тайма`}
              inputProps={{
                style: { backgroundColor: theme.colors.GrayF2 },
                defaultValue: timestampUrls[keyFirstPeriod]?.url ?? '',
              }}
            />
            <UrlInput
              ref={secondPeriodRef}
              isLoading={isLoading}
              name="second-time-url"
              placeholder={t`Ссылка на начало 2-ого тайма`}
              inputProps={{
                style: { backgroundColor: theme.colors.GrayF2 },
                defaultValue: timestampUrls[keySecondPeriod]?.url ?? '',
              }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <UDButton variant={'primary'} onClick={handleReplaceAllEventUrl}>
            <Trans>Заменить существующие</Trans>
          </UDButton>
          <UDButton variant={'secondary'} onClick={handleReplaceEmptyEventUrl}>
            <Trans>Вставить в пустые</Trans>
          </UDButton>
        </Modal.Footer>
      </Modal>
    </>
  );
}
